import { useIsMobile } from "@/hooks/useIsMobile";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { useLocation } from "react-router-dom";

const AUTHENTICATION_ROUTES = new Set([
  "/login",
  "/forgot-password",
  "/reset-password",
  "/auth/confirm",
  "/auth/callback",
]);

interface NavigatorUAData {
  mobile: boolean;
}

/**
 * Checks if the device is a mobile device using user-agent metadata.
 * Uses the modern Navigator.userAgentData API with fallback to classic UA string.
 */
function getIsMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;

  // Modern API (Chromium 90+, Edge 90+)
  const nav = navigator as Navigator & { userAgentData?: NavigatorUAData };
  if (nav.userAgentData) {
    return nav.userAgentData.mobile === true;
  }

  // Fallback: regex check on classic UA string
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent);
}

/**
 * Full-screen blocking overlay for mobile browsers.
 *
 * Uses dual detection (viewport width < 768px OR mobile user-agent) to show
 * a branded "Mobile Version Coming Soon" screen instead of the application.
 * Returns null on desktop — completely transparent to the desktop experience.
 */
export function MobileBlockingScreen() {
  const isMobileViewport = useIsMobile();
  const isMobileDevice = getIsMobileDevice();
  const { pathname, search } = useLocation();
  // Remote clients may open OAuth/approval in a phone browser. These pages
  // retain their usual authentication and permission gates inside Routes.
  const connectorFlow =
    pathname === "/connector/authorize" ||
    /^\/connector\/(actions|uploads)\/[^/]+$/.test(pathname) ||
    (pathname === "/settings" && new URLSearchParams(search).get("section") === "connections") ||
    AUTHENTICATION_ROUTES.has(pathname);

  if (connectorFlow || (!isMobileViewport && !isMobileDevice)) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#1a1a1a] px-8">
      {/* Logo + brand */}
      <div className="flex items-center gap-3">
        <FranklinLogo size="xl" showBorder={false} showShadow={false} />
        <div className="flex flex-col">
          <span className="text-3xl font-bold text-primary">Franklin</span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/80">
            The DNA of Buildings
          </span>
        </div>
      </div>

      {/* Heading */}
      <h1 className="mt-8 text-center text-2xl font-bold text-white">Mobile Version Coming Soon</h1>

      {/* Body */}
      <p className="mt-4 max-w-sm text-center text-base leading-relaxed text-gray-400">
        We're still working on the mobile experience. Please visit this site on a desktop or tablet
        for the best experience.
      </p>

      {/* Footer */}
      <p className="mt-6 text-center text-sm text-gray-600">Thanks for your patience!</p>
    </div>
  );
}
